import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useApiQuery } from '../../../utils/api'
import { Loading, Error } from '../../Elements/Indicators'
import { PageHeader } from '../../Elements/PageHeader'
import { UserAnalysis } from '../../Elements/Charts'
import ProjectUserSelector from '../Projects/ProjectUserSeletor'
import { JiraUser } from '@jirax/common'

// Define interfaces
interface RouteParams extends Record<string, string | undefined> {
  userId: string
  projectKey?: string
  projKey?: string
}

interface Project {
  id: string
  key: string
  name: string
  users: JiraUser[]
  boards: Array<{ id: string; name: string }>
}

// User interface expected by <UserAnalysis>
interface User {
  id: string
  name: string
  avatar?: string
  role?: string
}

interface BreadcrumbItem {
  label: string
  to?: string
}

interface BackLink {
  label: string
  to: string
}

interface HeaderConfig {
  pageTitle: string
  backLink: BackLink
  breadcrumb: BreadcrumbItem[]
}

const UserStats: React.FC = () => {
  const { userId, projectKey, projKey } = useParams<RouteParams>()
  const [user, setUser] = useState<JiraUser | null>(null)
  const navigate = useNavigate()
  const location = useLocation()

  // ---------------------------------------------------------------------------
  // Parameter guard – must be computed _before_ any conditional hook returns
  // ---------------------------------------------------------------------------
  const actualProjectKey = projectKey || projKey
  const hasParams = Boolean(userId && actualProjectKey)

  // Determine if we came from projects or users page
  const isFromProjects = location.pathname.includes('/projects/')

  // Fetch project data
  const {
    isLoading: isLoadingProject,
    error: projectError,
    data: project
  } = useApiQuery<Project>(['projects', actualProjectKey], {
    enabled: hasParams
  })

  // Find the user in the project
  useEffect(() => {
    if (project && Array.isArray(project.users)) {
      // JiraUser only contains accountId → compare against that
      const foundUser = project.users.find(u => u.accountId === userId)

      setUser(foundUser || null)
    }
  }, [project, userId])

  // Handle user selection change - navigate to the appropriate route based on context
  const onChangeUser = (newUserId: string): void => {
    if (isFromProjects && projKey) {
      // If we came from projects, maintain that context
      navigate(`/projects/${projKey}/users/${newUserId}/stats`)
    } else {
      // Otherwise use the users route
      navigate(`/users/${newUserId}/stats/${actualProjectKey}`)
    }
  }

  // Create breadcrumb based on where we came from
  const createHeaderConfig = (): HeaderConfig => {
    if (isFromProjects && projKey) {
      return {
        pageTitle: 'User Productivity Report',
        backLink: {
          label: 'Back to Project',
          to: `/projects/${projKey}`
        },
        breadcrumb: [
          {
            label: 'Projects',
            to: '/projects'
          },
          {
            label: project?.name || 'Project',
            to: `/projects/${projKey}`
          },
          {
            label: 'User Stats'
          }
        ]
      }
    } else {
      return {
        pageTitle: 'User Productivity Report',
        backLink: {
          label: 'Back to Users',
          to: '/users'
        },
        breadcrumb: [
          {
            label: 'Users',
            to: '/users'
          },
          {
            label: 'User Stats'
          }
        ]
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Early exit *after* all hooks have been declared
  // ---------------------------------------------------------------------------
  if (!hasParams) {
    return (
      <Error
        title={'Error Loading User Stats'}
        message={'User ID and Project Key are required'}
      />
    )
  }

  if (isLoadingProject) {
    return <Loading message={`Loading project ${actualProjectKey} and user data.`} />
  }

  if (projectError) {
    return <Error title={'Error Loading Project'} message={(projectError as Error).message} />
  }

  if (!project) {
    return <Error title={'Error Loading Project'} message={'Project data is missing'} />
  }

  if (!user) {
    return <Error title={'Error Loading User'} message={`User with ID ${userId} not found in project ${actualProjectKey}`} />
  }

  // Get the first board ID for analysis
  const boardId = project.boards && project.boards.length > 0
    ? project.boards[0].id
    : ''

  if (!boardId) {
    return <Error title={'Error Loading Board'} message={'No boards found for this project'} />
  }

  const headerConfig = createHeaderConfig()

  // Map JiraUser to the simplified User shape required by <UserAnalysis>
  const userForAnalysis: User = {
    id: user.accountId,
    name: user.displayName,
    avatar: user.avatarUrl
  }

  return (
    <>
      <PageHeader config={headerConfig} />
      <main className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        <ProjectUserSelector
          project={project}
          onChangeUser={onChangeUser}
          user={user}
        />
        <div className='min-h-max rounded-lg'>
          <UserAnalysis
            user={userForAnalysis}
            boardId={boardId}
            projectKey={actualProjectKey || ''}
          />
        </div>
      </main>
    </>
  )
}

export default UserStats
